import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chip, type ChipVariant } from '../../components/ui/Chip';
import { TableRowSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import {
  useCampaigns,
  useArchiveCampaign,
  useDeleteCampaign,
  usePauseCampaign,
  useResumeCampaign,
  type Campaign,
  type CampaignStatus,
  type CampaignType,
} from '../../api/campaigns.api';
import { useOrgSettings } from '../../api/org-settings.api';
import { availableToOrg } from '../../lib/org-type';

/* ── Screen 12 — Campaigns list ── */

const STATUS_OPTIONS: { v: '' | CampaignStatus; l: string }[] = [
  { v: '', l: 'All statuses' },
  { v: 'DRAFT', l: 'Draft' },
  { v: 'ACTIVE', l: 'Active' },
  { v: 'PAUSED', l: 'Paused' },
  { v: 'COMPLETED', l: 'Completed' },
  { v: 'ARCHIVED', l: 'Archived' },
];

const TYPE_OPTIONS: { v: '' | CampaignType; l: string }[] = [
  { v: '', l: 'All types' },
  { v: 'CANDIDATE', l: 'Candidate' },
  { v: 'BD', l: 'BD' },
  { v: 'JOB', l: 'Job' },
  { v: 'DEAL', l: 'Deal' },
  { v: 'EVENT', l: 'Event' },
  { v: 'NEWSLETTER', l: 'Newsletter' },
];

const STATUS_TO_CHIP: Record<CampaignStatus, { variant: ChipVariant; label: string }> = {
  DRAFT: { variant: 'draft', label: 'Draft' },
  ACTIVE: { variant: 'live', label: 'Live' },
  PAUSED: { variant: 'paused', label: 'Paused' },
  COMPLETED: { variant: 'completed', label: 'Completed' },
  ARCHIVED: { variant: 'archived', label: 'Archived' },
};

const TYPE_LABEL: Record<CampaignType, string> = {
  CANDIDATE: 'Candidate',
  BD: 'BD',
  JOB: 'Job',
  DEAL: 'Deal',
  EVENT: 'Event',
  NEWSLETTER: 'Newsletter',
  SMS_BLAST: 'SMS blast',
};

export function CampaignsPage() {
  const [statusFilter, setStatusFilter] = useState<'' | CampaignStatus>('');
  const [typeFilter, setTypeFilter] = useState<'' | CampaignType>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, isError, refetch } = useCampaigns({
    status: statusFilter,
    type: typeFilter,
    page,
    limit,
  });

  const allRows = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((c) => c.name.toLowerCase().includes(q));
  }, [allRows, search]);

  const hasAnyCampaigns = total > 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <Breadcrumb />
      <HeadingRow total={total} />

      {hasAnyCampaigns && (
        <FilterRow
          search={search}
          onSearch={setSearch}
          status={statusFilter}
          onStatus={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          type={typeFilter}
          onType={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
        />
      )}

      <section
        style={{
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 'var(--card-radius)',
          boxShadow: 'var(--card-shadow)',
          overflow: 'hidden',
        }}
      >
        {isError ? (
          <div style={{ padding: 'var(--space-6)' }}>
            <EmptyState
              icon={<IconAlert size={20} />}
              title="Couldn't load campaigns"
              description="Something went wrong loading your campaigns. Try again."
              action={
                <SecondaryButton onClick={() => refetch()}>
                  <IconRefresh size={14} />
                  <span style={{ marginLeft: 'var(--space-1)' }}>Retry</span>
                </SecondaryButton>
              }
            />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 'var(--space-4)' }}>
            <TableRowSkeleton rows={6} />
          </div>
        ) : !hasAnyCampaigns ? (
          <EmptyState
            icon={<IconSend size={20} />}
            title="No campaigns yet"
            description="Launch your first outbound sequence to start engaging candidates."
            action={
              <Link to="/campaigns/new" style={{ textDecoration: 'none' }}>
                <PrimaryButton>
                  <IconPlus size={14} />
                  <span style={{ marginLeft: 'var(--space-1)' }}>New campaign</span>
                </PrimaryButton>
              </Link>
            }
          />
        ) : rows.length === 0 ? (
          <div style={{ padding: 'var(--space-6)' }}>
            <EmptyState
              icon={<IconSearch size={20} />}
              title="No matches"
              description="No campaigns match your current filters. Try clearing them."
              action={
                <SecondaryButton
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('');
                    setTypeFilter('');
                    setPage(1);
                  }}
                >
                  Clear filters
                </SecondaryButton>
              }
            />
          </div>
        ) : (
          <CampaignsTable rows={rows} />
        )}
      </section>

      {hasAnyCampaigns && totalPages > 1 && (
        <PaginationFooter
          shown={rows.length}
          total={total}
          page={page}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </div>
  );
}

/* ── Breadcrumb / heading ── */

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li>
          <Link
            to="/dashboard"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}
          >
            Engage
          </Link>
        </li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}>
          <IconChevronRight size={12} />
        </li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
          Campaigns
        </li>
      </ol>
    </nav>
  );
}

function HeadingRow({ total }: { total: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          Campaigns
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          {total === 0
            ? 'No campaigns yet.'
            : `${total} ${total === 1 ? 'campaign' : 'campaigns'} across your org.`}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <Link to="/campaigns/new" style={{ textDecoration: 'none' }}>
          <PrimaryButton>
            <IconPlus size={14} />
            <span style={{ marginLeft: 'var(--space-1)' }}>New campaign</span>
          </PrimaryButton>
        </Link>
      </div>
    </div>
  );
}

/* ── Filter row ── */

function FilterRow({
  search,
  onSearch,
  status,
  onStatus,
  type,
  onType,
}: {
  search: string;
  onSearch: (v: string) => void;
  status: '' | CampaignStatus;
  onStatus: (v: '' | CampaignStatus) => void;
  type: '' | CampaignType;
  onType: (v: '' | CampaignType) => void;
}) {
  const jtOrgType = useOrgSettings().data?.data?.jt_org_type;
  const typeOptions = TYPE_OPTIONS.filter((o) => !o.v || availableToOrg(o.v, jtOrgType));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: '240px', maxWidth: '340px', position: 'relative' }}>
        <span aria-hidden="true" style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-subtle)' }}>
          <IconSearch size={14} />
        </span>
        <input
          type="search"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          style={{
            width: '100%',
            height: 'var(--input-height)',
            paddingLeft: 'var(--space-8)',
            paddingRight: 'var(--space-3)',
            border: '1px solid var(--input-border)',
            borderRadius: 'var(--input-radius)',
            backgroundColor: 'var(--input-bg)',
            fontSize: 'var(--input-font-size)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-primary)',
            outline: 'none',
          }}
        />
      </div>
      <DropdownSelect label="Status" value={status} onChange={(v) => onStatus(v as '' | CampaignStatus)} options={STATUS_OPTIONS.map((o) => ({ v: o.v, l: o.l }))} />
      <DropdownSelect label="Type" value={type} onChange={(v) => onType(v as '' | CampaignType)} options={typeOptions.map((o) => ({ v: o.v, l: o.l }))} />
    </div>
  );
}

function DropdownSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { v: string; l: string }[];
}) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        style={{
          height: 'var(--input-height)',
          padding: '0 var(--space-8) 0 var(--space-3)',
          border: '1px solid var(--input-border)',
          borderRadius: 'var(--input-radius)',
          backgroundColor: 'var(--input-bg)',
          fontSize: 'var(--input-font-size)',
          fontFamily: 'var(--font-sans)',
          color: 'var(--color-text-primary)',
          outline: 'none',
          appearance: 'none',
          cursor: 'pointer',
        }}
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
      <span aria-hidden="true" style={{ position: 'absolute', right: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--color-text-muted)' }}>
        <IconChevronDown size={12} />
      </span>
    </div>
  );
}

/* ── Table ── */

function CampaignsTable({ rows }: { rows: Campaign[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
        <thead>
          <tr>
            <Th>Campaign</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Updated</Th>
            <Th align="right" style={{ width: '200px' }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <CampaignRow key={row.id} row={row} isFirst={i === 0} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignRow({ row, isFirst }: { row: Campaign; isFirst: boolean }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const archive = useArchiveCampaign();
  const del = useDeleteCampaign();
  const pause = usePauseCampaign();
  const resume = useResumeCampaign();

  const chipMeta = STATUS_TO_CHIP[row.status];
  const isDraft = row.status === 'DRAFT';
  const isActive = row.status === 'ACTIVE';
  const isPaused = row.status === 'PAUSED';

  const onArchive = () => {
    archive.mutate(row.id, {
      onSuccess: () => addToast({ variant: 'success', title: 'Campaign archived' }),
      onError: () => addToast({ variant: 'danger', title: 'Could not archive', description: 'Try again in a moment.' }),
    });
  };

  const onDelete = () => {
    if (!confirm(`Delete campaign "${row.name}"? This cannot be undone.`)) return;
    del.mutate(row.id, {
      onSuccess: () => addToast({ variant: 'success', title: 'Campaign deleted' }),
      onError: () => addToast({ variant: 'danger', title: 'Could not delete', description: 'Try again in a moment.' }),
    });
  };

  const onPauseToggle = () => {
    const action = isActive ? pause : resume;
    const verb = isActive ? 'paused' : 'resumed';
    action.mutate(row.id, {
      onSuccess: () => addToast({ variant: 'success', title: `Campaign ${verb}` }),
      onError: () => addToast({ variant: 'danger', title: `Could not ${isActive ? 'pause' : 'resume'}` }),
    });
  };

  return (
    <tr
      style={{
        borderTop: isFirst ? 'none' : '1px solid var(--color-border-default)',
        transition: 'background-color var(--transition-base)',
        cursor: 'pointer',
      }}
      onClick={() => navigate(isDraft ? `/campaigns/${row.id}/edit` : `/campaigns/${row.id}`)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--table-row-hover-bg)';
        const actions = e.currentTarget.querySelector<HTMLElement>('[data-hover-actions]');
        if (actions) actions.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        const actions = e.currentTarget.querySelector<HTMLElement>('[data-hover-actions]');
        if (actions) actions.style.opacity = '0';
      }}
    >
      <Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
            {row.name}
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Created {formatRelative(row.created_at)}
          </span>
        </div>
      </Td>
      <Td>
        <TypeChip type={row.type} />
      </Td>
      <Td>
        <Chip variant={chipMeta.variant}>{chipMeta.label}</Chip>
      </Td>
      <Td>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {formatRelative(row.updated_at)}
        </span>
      </Td>
      <Td align="right">
        <div
          data-hover-actions
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', opacity: 0, transition: 'opacity var(--transition-base)' }}
        >
          {(isActive || isPaused) && (
            <SmallButton onClick={onPauseToggle} disabled={pause.isPending || resume.isPending}>
              {isActive ? <IconPause size={11} /> : <IconPlay size={11} />}
              <span style={{ marginLeft: 'var(--space-1)' }}>{isActive ? 'Pause' : 'Resume'}</span>
            </SmallButton>
          )}
          <Link
            to={isDraft ? `/campaigns/${row.id}/edit` : `/campaigns/${row.id}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              height: 'var(--btn-height-sm)',
              padding: '0 var(--btn-padding-x-sm)',
              borderRadius: 'var(--btn-radius)',
              backgroundColor: 'var(--color-action-primary)',
              color: 'var(--color-action-primary-text)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              textDecoration: 'none',
            }}
          >
            {isDraft ? <IconEdit size={11} /> : <IconEye size={11} />}
            {isDraft ? 'Continue' : 'Open'}
          </Link>
          {isDraft ? (
            <SmallButton onClick={onDelete} disabled={del.isPending} variant="danger">
              <IconTrash size={11} />
            </SmallButton>
          ) : row.status !== 'ARCHIVED' ? (
            <SmallButton onClick={onArchive} disabled={archive.isPending}>
              <IconArchive size={11} />
            </SmallButton>
          ) : null}
        </div>
      </Td>
    </tr>
  );
}

function Th({ children, align = 'left', style }: { children?: ReactNode; align?: 'left' | 'right'; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        textAlign: align,
        height: 'var(--table-header-height)',
        padding: '0 var(--table-cell-padding-x)',
        backgroundColor: 'var(--table-header-bg)',
        color: 'var(--table-header-text)',
        fontSize: 'var(--table-header-size)',
        fontWeight: 'var(--table-header-weight)',
        letterSpacing: 'var(--table-header-tracking)',
        textTransform: 'uppercase',
        borderBottom: '1px solid var(--color-border-default)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <td
      style={{
        padding: 'var(--table-cell-padding-y) var(--table-cell-padding-x)',
        textAlign: align,
        verticalAlign: 'middle',
        height: 'var(--table-row-height)',
      }}
    >
      {children}
    </td>
  );
}

function TypeChip({ type }: { type: CampaignType }) {
  const map: Record<CampaignType, { bg: string; fg: string; border: string }> = {
    CANDIDATE: { bg: 'var(--color-blue-50)', fg: 'var(--color-brand-blue)', border: 'var(--color-blue-100)' },
    BD: { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)', border: 'var(--color-warning-border)' },
    JOB: { bg: 'var(--color-indigo-50)', fg: 'var(--color-brand-indigo)', border: 'var(--color-indigo-100)' },
    DEAL: { bg: 'var(--color-teal-50)', fg: 'var(--color-teal-600)', border: 'var(--color-teal-100)' },
    EVENT: { bg: 'var(--color-navy-50)', fg: 'var(--color-brand-navy)', border: 'var(--color-navy-100)' },
    NEWSLETTER: { bg: 'var(--color-surface-muted)', fg: 'var(--color-text-secondary)', border: 'var(--color-border-default)' },
    SMS_BLAST: { bg: 'var(--color-indigo-50)', fg: 'var(--color-brand-indigo)', border: 'var(--color-indigo-100)' },
  };
  const s = map[type];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--chip-height)',
        padding: 'var(--chip-padding-y) var(--chip-padding-x)',
        borderRadius: 'var(--chip-radius)',
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        color: s.fg,
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--chip-font-size)',
        fontWeight: 'var(--chip-font-weight)',
        letterSpacing: 'var(--tracking-wide)',
      }}
    >
      {TYPE_LABEL[type]}
    </span>
  );
}

/* ── Pagination ── */

function PaginationFooter({
  shown,
  total,
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  shown: number;
  total: number;
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
        Showing {shown} of {total} · page {page} of {totalPages}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        <SmallButton onClick={onPrev} disabled={page <= 1}>
          <IconChevronLeft size={12} />
          <span style={{ marginLeft: 'var(--space-1)' }}>Prev</span>
        </SmallButton>
        <SmallButton onClick={onNext} disabled={page >= totalPages}>
          Next <IconChevronRight size={12} />
        </SmallButton>
      </div>
    </div>
  );
}

/* ── Buttons ── */

function PrimaryButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        border: 'none',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function SmallButton({
  children,
  disabled,
  onClick,
  variant = 'default',
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  variant?: 'default' | 'danger';
}) {
  const isDanger = variant === 'danger';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: isDanger ? 'var(--color-danger-bg)' : 'var(--color-action-secondary)',
        color: isDanger ? 'var(--color-danger)' : 'var(--color-action-secondary-text)',
        border: `1px solid ${isDanger ? 'var(--color-danger-border)' : 'var(--color-action-secondary-border)'}`,
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

/* ── Helpers ── */

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMins = Math.round(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.round(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.round(diffHrs / 24);
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

/* ── Icons ── */

function IconChevronRight({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconChevronLeft({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>; }
function IconChevronDown({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>; }
function IconPlus({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function IconSearch({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function IconSend({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>; }
function IconEye({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function IconEdit({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>; }
function IconPause({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>; }
function IconPlay({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>; }
function IconArchive({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>; }
function IconTrash({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1.5 14a2 2 0 0 1-2 2H8.5a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>; }
function IconAlert({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>; }
function IconRefresh({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>; }
